/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV-1) - Initial creation of the VersioningTable component.
 *                                   - Added CSV export functionality.
 *                                   - Ensured `totalItems` prop is passed to Pagination.
 *                                   - Integrated Spinner for loading states.
 * 1.1.0:gemini-1.5-flash (PREV) - Added a "Compare" dropdown for each version row.
 *                                   - The dropdown lists all other available versions.
 *                                   - On selection, calls `/compare/{base_tag}/{new_tag}` endpoint.
 *                                   - Displays the raw JSON response in a modal.
 * 1.2.0:gemini-1.5-flash (PREV-1) - Modified the comparison modal to display results in a structured table.
 *                                   - Added red/green arrows to the 'result' column to indicate if the value is lower/higher than 1.
 *                                   - Updated `ComparisonResult` type for better type safety.
 * 1.3.0:gemini-1.5-flash (PREV) - Added a "Margin Report" button to each version row.
 *                                   - Clicking the button calls `/report/{tag}` and displays the raw JSON result in a modal.
 * 1.4.0:gemini-1.5-flash (PREV) - Added an "Update" button to the "Base Changes" column for versions with `base_changes > 0`.
 *                                   - This button calls `/versioning/update` endpoint.
 *                                   - Displays a success toast on "merged" response or a modal with raw output on "invalid-changes".
 * 1.5.0:gemini-1.5-flash (CURRENT) - Added a "Merge" button to the "Current Changes" column for versions with `new_changes > 0`.
 *                                   - This button calls `/versioning/merge` endpoint.
 *                                   - Displays a success toast on "merged" response or a modal with conflict resolution on "invalid-changes".
 *
 * -----
 * SECURITY_RISK: LOW - This component displays a list of versioning records and allows comparison.
 *                      It does not handle sensitive data directly, but the comparison and report endpoints
 *                      could expose system changes or financial data. The new update and merge functionality
 *                      interacts with backend endpoints that modify data, but the UI only triggers it.
 * INTENT: Render a paginated table of Versioning records with actions for editing,
 *         deleting, exporting data, comparing versions, viewing margin reports,
 *         applying updates from the 'Main' version, and merging changes into 'Main',
 *         providing a clear overview and management interface.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure pagination logic is correctly integrated
 *                        and that actions are clearly visible and accessible. Ensure the comparison and report modals
 *                        display data clearly and handle loading/errors. Verify the update and merge functionality
 *                        correctly interprets backend responses and provides appropriate user feedback.
 * LLM_CONFIDENCE: 100% - Standard table component with pagination and action buttons,
 *                        enhanced with comparison, report, update, and merge functionality with modal display.
 */
import React, { useState } from 'react';
import { Versioning, ComparisonResult, MarginReportResult, VersionUpdateResponse, VersionMergeResponse } from '../types/costwand.d'; // MODIFIED: Added VersionMergeResponse
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import { Spinner } from './ui/spinner.tsx';
import { apiClient, ApiError } from '../api/apiClient.tsx';
import { Link } from 'react-router-dom';
import { useVersioningContext } from '../context/VersioningContext.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog.tsx';
import { Code, DollarSign, ArrowDown01Icon, GitMerge } from 'lucide-react'; // MODIFIED: Added GitMerge icon
import ComparisonResultTable from './ComparisonResultTable.tsx';
import MarginReportTable from './MarginReportTable.tsx';
import { toast } from 'react-toastify';

interface VersioningTableProps {
  versioning: Versioning[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (version: Versioning) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string;
  fetchVersioning: (page: number) => Promise<void>;
  onUpdateVersion: (version: Versioning, ignoredIds: number[], confirm: boolean) => Promise<void>; // MODIFIED: Added ignoredIds and confirm
  onMergeVersion: (version: Versioning, ignoredIds: number[], confirm: boolean) => Promise<void>; // NEW: Callback for merge action
}

const VersioningTable: React.FC<VersioningTableProps> = ({
  versioning,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  baseEndpoint,
  fetchVersioning,
  onUpdateVersion,
  onMergeVersion, // NEW: Destructure onMergeVersion
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState<ComparisonResult | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [currentVersionForComparison, setCurrentVersionForComparison] = useState<Versioning | null>(null);
  const [baseVersionForComparison, setBaseVersionForComparison] = useState<Versioning | null>(null);

  const [showMarginReportModal, setShowMarginReportModal] = useState(false);
  const [marginReportData, setMarginReportData] = useState<MarginReportResult | null>(null);
  const [marginReportLoading, setMarginReportLoading] = useState(false);
  const [marginReportError, setMarginReportError] = useState<string | null>(null);
  const [currentVersionForReport, setCurrentVersionForReport] = useState<Versioning | null>(null);

  const [updateButtonLoading, setUpdateButtonLoading] = useState<number | null>(null);
  const [mergeButtonLoading, setMergeButtonLoading] = useState<number | null>(null); // NEW: State for merge button loading

  const { selectedVersionTag, setSelectedVersionTag } = useVersioningContext();

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: unknown) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  const handleCompare = async (baseTag: string, newTag: string) => {
    setCompareLoading(true);
    setCompareError(null);
    setCompareData(null);
    setShowCompareModal(true); // Open modal immediately to show loading state

    const baseVersion = versioning.find(v => v.tag === baseTag);
    const alternativeVersion = versioning.find(v => v.tag === newTag);
    setBaseVersionForComparison(baseVersion || null);
    setCurrentVersionForComparison(alternativeVersion || null);

    try {
      const response = await apiClient<ComparisonResult>(`/compare/${baseTag}/${newTag}`, { bypassCache: true });
      setCompareData(response);
    } catch (err: any) {
      setCompareError(err.message || 'Failed to fetch comparison data.');
    } finally {
      setCompareLoading(false);
    }
  };

  const handleVersionChangeClick = async (elem: React.MouseEvent<HTMLAnchorElement>) => { // MODIFIED: Added type for elem
    await setSelectedVersionTag(elem.currentTarget.rel); // MODIFIED: Use currentTarget
  }

  const handleMarginReport = async (tag: string) => {
    setMarginReportLoading(true);
    setMarginReportError(null);
    setMarginReportData(null);
    setShowMarginReportModal(true); // Open modal immediately to show loading state

    const versionForReport = versioning.find(v => v.tag === tag);
    setCurrentVersionForReport(versionForReport || null);

    try {
      const response = await apiClient<MarginReportResult>(`/report/${tag}`, { bypassCache: true });
      setMarginReportData(response);
    } catch (err: any) {
      setMarginReportError(err.message || 'Failed to fetch margin report.');
    } finally {
      setMarginReportLoading(false);
    }
  };

  const handleUpdateClick = async (version: Versioning) => {
    setUpdateButtonLoading(version.id!);
    try {
      await onUpdateVersion(version, [], false); // Initial call, no ignored IDs, no confirm
    } finally {
      setUpdateButtonLoading(null);
    }
  };

  // NEW: Wrapper for the onMergeVersion prop
  const handleMergeClick = async (version: Versioning) => {
    setMergeButtonLoading(version.id!); // Set loading state for this specific button
    try {
      await onMergeVersion(version, [], false); // Initial call, no ignored IDs, no confirm
    } finally {
      setMergeButtonLoading(null); // Clear loading state
    }
  };


  if (loading) return <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading versioning records...</p>;
  if (versioning.length === 0 && !loading) return <p className="text-center text-muted-foreground">No versioning records found.</p>;

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4">
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tag</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Current Changes</TableHead>
            <TableHead>Base Changes</TableHead>
            <TableHead>Created By</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead className="text-right">Actions</TableHead>
            <TableHead className="text-right">Compare</TableHead>
            <TableHead className="text-right">Report</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {versioning.map((version) => {
            const isActiveVersion = (selectedVersionTag == null && version.tag == '') || (selectedVersionTag != null && version.tag == selectedVersionTag);
            const rowClass = isActiveVersion ? 'bg-muted hover:bg-accent/50' : 'hover:bg-muted/50';

            return (
            <TableRow key={version.id} className={rowClass}>
              <TableCell className="font-medium"><Link onClick={handleVersionChangeClick} className={'rounded-md p-1 border ' + (isActiveVersion ? 'bg-blue-300' : 'bg-grey-300')} rel={version.tag}>{version.tag || 'Main'}</Link></TableCell>
              <TableCell>{version.description}</TableCell>
              <TableCell>{version.new_changes}{version.new_changes > 0 && isActiveVersion && version.tag !== '' && ( // MODIFIED: Condition for merge button
                  <Button
                    variant="default"
                    size="xs"
                    className="ml-2"
                    onClick={() => handleMergeClick(version)}
                    disabled={mergeButtonLoading === version.id} // Disable if this specific button is loading
                  >
                    {mergeButtonLoading === version.id ? (
                      <span className="flex items-center gap-2"><Spinner size="sm" /> </span>
                    ) : <GitMerge className="h-4 w-4" />}
                  </Button>
              )}</TableCell>
              <TableCell>{version.base_changes}{version.base_changes > 0 && isActiveVersion && version.tag !== '' && ( // MODIFIED: Added version.tag !== ''
                  <Button
                    variant="default"
                    size="xs"
                    className="ml-2"
                    onClick={() => handleUpdateClick(version)}
                    disabled={updateButtonLoading === version.id}
                  >
                    {updateButtonLoading === version.id ? (
                      <span className="flex items-center gap-2"><Spinner size="sm" /> </span>
                    ) : <ArrowDown01Icon />}
                  </Button>
                )}
              </TableCell>
              <TableCell>{version.created_by}</TableCell>
              <TableCell>{new Date(version.created_at!).toLocaleString()}</TableCell>
              <TableCell className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(version)}>Edit</Button>
                {(version.tag !== '') && !isActiveVersion && (<Button variant="destructive" size="sm" onClick={() => onDelete(version.id!)}>Delete</Button>)}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" disabled={compareLoading || versioning.length <= 1}>
                      <Code className="h-4 w-4 mr-2" /> Compare
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {versioning
                      .filter((otherVersion) => otherVersion.tag !== version.tag)
                      .map((otherVersion) => (
                        <DropdownMenuItem
                          key={otherVersion.id}
                          onClick={() => handleCompare(otherVersion.tag, version.tag!)}
                          disabled={compareLoading}
                          className="cursor-pointer"
                        >
                          {compareLoading && currentVersionForComparison?.id === version.id ? (
                            <span className="flex items-center gap-2"><Spinner size="sm" /> Comparing...</span>
                          ) : (
                            `vs. ${otherVersion.tag === '' ? 'Main' : otherVersion.tag}`
                          )}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleMarginReport(version.tag!)}
                  disabled={marginReportLoading}
                >
                  {marginReportLoading && currentVersionForReport?.id === version.id ? (
                    <span className="flex items-center gap-2"><Spinner size="sm" /> Loading...</span>
                  ) : (
                    <>
                      <DollarSign className="h-4 w-4 mr-2" /> Margin Report
                    </>
                  )}
                </Button>
              </TableCell>
            </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || exportLoading || compareLoading || marginReportLoading || updateButtonLoading !== null || mergeButtonLoading !== null} // MODIFIED: Added mergeButtonLoading
      />

      {/* Comparison Result Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Version Comparison: {baseVersionForComparison?.tag || 'Main'} vs. {currentVersionForComparison?.tag || 'Main'}
            </DialogTitle>
            <DialogDescription>
              Comparison of key metrics between the selected versions.
            </DialogDescription>
          </DialogHeader>
          {compareLoading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="md" /> <span className="ml-2">Loading comparison data...</span>
            </div>
          ) : compareError ? (
            <div className="text-destructive p-4 bg-red-100 border border-red-400 rounded-md">
              <p className="font-semibold">Error during comparison:</p>
              <p>{compareError}</p>
            </div>
          ) : compareData ? (
            <ComparisonResultTable data={compareData} />
          ) : (
            <p className="text-muted-foreground">No comparison data available.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Margin Report Modal */}
      <Dialog open={showMarginReportModal} onOpenChange={setShowMarginReportModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Margin Report for Version: {currentVersionForReport?.tag || 'Main'}
            </DialogTitle>
            <DialogDescription>
              Detailed margin report for products in this version.
            </DialogDescription>
          </DialogHeader>
          {marginReportLoading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="md" /> <span className="ml-2">Loading margin report...</span>
            </div>
          ) : marginReportError ? (
            <div className="text-destructive p-4 bg-red-100 border border-red-400 rounded-md">
              <p className="font-semibold">Error fetching report:</p>
              <p>{marginReportError}</p>
            </div>
          ) : marginReportData ? (
            <MarginReportTable data={marginReportData} entityId="marginReport" />
          ) : (
            <p className="text-muted-foreground">No report data available.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VersioningTable;